import { map } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { ImageAttachment } from "../../../../../@types/activities";
import { lazy, Suspense, useState } from "react";
import { useEagerPrintImages } from "../../../ActivityPrintViewContext.ts";

const ImagesGalleryPage = lazy(
  () => import("../../../../../components/shared/ImagesGalleryPage"),
);

type ImageAttachmentsArgs = {
  images: ImageAttachment[];
};

export default function ImageAttachments({ images }: ImageAttachmentsArgs) {
  const [galleryState, setGalleryState] = useState({
    isOpen: false,
    selectedImageId: null,
  });
  const mainImage = images[0];

  const lastImageIndex = images.length > 8 ? 8 : 9;
  const thumbnailImages = images.slice(1, lastImageIndex);
  // Print shows every image, the others below the main image as square tiles
  const printTileImages = images.slice(1);
  // When opened for printing they must be loaded before printing; otherwise
  // the screen must not download images it never shows
  const eagerPrintImages = useEagerPrintImages();

  const handleImageClick = (imageId) => {
    setGalleryState({
      isOpen: true,
      selectedImageId: imageId,
    });
  };

  const handleCloseGallery = () => {
    setGalleryState({
      isOpen: false,
      selectedImageId: null,
    });
  };

  const galleryImages = images.map(({ id }) => ({ id }));

  return (
    <div className="activity-images attachments">
      <div className="images-view activity-object-image flex flex-1 gap-2 flex-wrap justify-center mb-4 empty:hidden print:hidden">
        <div className="empty:hidden image-preview preview preview-large w-full flex justify-center">
          <button
            type="button"
            onClick={() => {
              handleImageClick(mainImage.id);
            }}
          >
            <img
              className="rounded"
              src={mainImage.large_thumb}
              alt={mainImage.original_filename}
            />
          </button>
        </div>
        {map(thumbnailImages, (image, index) => (
          <div
            key={index}
            className="empty:hidden image-preview preview preview-small"
          >
            <button
              type="button"
              onClick={() => {
                handleImageClick(image.id);
              }}
            >
              <img
                className="rounded"
                src={image.small_thumb}
                alt={image.original_filename}
              />
            </button>
          </div>
        ))}
        {images.length > 8 && (
          <button
            type="button"
            onClick={() => {
              handleImageClick(null);
            }}
          >
            <div className="image-preview preview preview-small w-[50px] h-[50px] text-center items-center bg-gray-200 rounded">
              <span className="preview-text leading-[50px]">
                <FontAwesomeIcon className="mr-1" icon={faPlus} size="sm" />
                {images.length - 8}
              </span>
            </div>
          </button>
        )}
      </div>
      {/* Print, like the PDF: the main image large, all others as square
          tiles, three per row (no "+N" tile) */}
      <div className="activity-images-print hidden print:block mb-4">
        <img
          className="block mx-auto max-w-[75%] max-h-[9cm] rounded"
          src={mainImage.large_thumb}
          alt={mainImage.original_filename}
        />
        {printTileImages.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-2">
            {map(printTileImages, (image, index) => (
              <img
                key={index}
                className="w-full aspect-square object-cover rounded"
                src={image.large_thumb}
                alt={image.original_filename}
                loading={eagerPrintImages ? undefined : "lazy"}
              />
            ))}
          </div>
        )}
      </div>
      {galleryState.isOpen && (
        <Suspense fallback={null}>
          <ImagesGalleryPage
            images={galleryImages}
            imageId={galleryState.selectedImageId || undefined}
            onClose={handleCloseGallery}
          />
        </Suspense>
      )}
    </div>
  );
}
